import React, { WheelEvent, useContext, useEffect, useState } from "react";
import { IntlShape, defineMessages, useIntl } from "react-intl";
import { Config, Pagetype } from "../../../../../../interfaces/Config";
import cn from "classnames";
import { commonMessages } from "../../../../../../utils/messages";
import { Exercise } from "@evidenceb/athena-common/interfaces/Exercise";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { commonMessages as athenaCommonMessages } from "@evidenceb/athena-common/helpers/messages";
import SafeguardedLink from "../../../../../../components/Player/Header/Components/SafeguardedLink/SafeguardedLink";
import VisuallyHidden from "../../../../../../components/VisuallyHidden/VisuallyHidden";
import { useUrl } from "../../../../../../hooks/usePage";
import {
    Activity,
    ActivityShell,
    Module,
    Objective,
    VisibilityStatus,
} from "@evidenceb/gameplay-interfaces";
import {
    getActivityById,
    getModuleById,
    getObjectiveById,
    getResourceIndex,
    getSublevelPool,
    initialTestFilter,
} from "../../../../../../utils/dataRetrieval";
import Case from "case";
import CustomSelect from "../../../../../../design-system-components/CustomSelect/CustomSelect";
import { configStore } from "../../../../../../contexts/ConfigContext";
import InfoPanelBottomSheet from "../../../../../../components/Player/InfoPanel/InfoPanelBottomSheet/InfoPanelBottomSheet";
import TeacherInfoPanelContent from "../TeacherInfoPanelContent/TeacherInfoPanelContent";
import { Data } from "../../../../../../interfaces/Data";
import {
    useResourceSeries,
    useResourceSeriesDispatch,
} from "../../../../../../components/Player/ResourceSeries/Contexts";
import { isResourceSeriesFinished } from "../../../../../../components/Player/ResourceSeries/utils";
import {
    moaeToHierarchy,
    resourceSeriesToMOAIds,
    useResourceSeriesMOA,
} from "../../../../../../components/Player/ResourceSeries/utils/moaResourceSeriesUtils";
import { ObjectiveNotVisibleError } from "../../../../../../errors";
import { useData } from "../../../../../../hooks/useData";
import { PlayerHeaderProps } from "../../../../../../interfaces/Player";

import "./TeacherHeader.scss";
import { useExercises } from "../../../../../../hooks/queries/useExercisesQuery";
import PlayerSpecimenBanner from "../../../../../../components/Specimen/PlayerSpecimenBanner/PlayerSpecimenBanner";
import { sessionStore } from "../../../../../../contexts/SessionContext";

const TeacherHeader = ({ aiType, opts, className }: PlayerHeaderProps) => {
    const {
        session: { specimen },
    } = useContext(sessionStore);
    const intl = useIntl();
    const moduleListUrl = useUrl(Pagetype.MODULELIST);

    const { isTabletOrSmaller } = useBreakpoints();
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    const resourceSeries = useResourceSeries();
    const { moduleId } = resourceSeriesToMOAIds(resourceSeries);

    const isWitchShell = opts?.shellType === ActivityShell.Witch;
    return (
        <>
            <header className={cn("teacher-header", className)}>
                {isTabletOrSmaller ? (
                    <div>
                        <div className="teacher-header__navigation">
                            <button
                                className="teacher-header__menu"
                                onClick={() => {
                                    setIsPanelOpen(!isPanelOpen);
                                }}
                            >
                                <Icon
                                    path="bie_bip"
                                    size="medium"
                                    color={
                                        // TODO #141414 Should be a css variable
                                        isWitchShell ? "#141414" : "white"
                                    }
                                    className="menu__icon"
                                />
                                <VisuallyHidden as="span">
                                    {intl.formatMessage(messages.openInfoPanel)}
                                </VisuallyHidden>
                            </button>
                            {isPanelOpen && (
                                <InfoPanelBottomSheet
                                    isOpen={isPanelOpen}
                                    setIsOpen={setIsPanelOpen}
                                >
                                    <TeacherInfoPanelContent />
                                </InfoPanelBottomSheet>
                            )}
                            <SafeguardedLink
                                to={moduleListUrl}
                                moduleId={moduleId}
                                className="teacher-header__exit"
                                opts={opts}
                                aiType={aiType}
                            >
                                <Icon
                                    path="close"
                                    size="medium"
                                    color={
                                        // TODO #141414 Should be a css variable
                                        isWitchShell ? "#141414" : "white"
                                    }
                                    className="exit__icon"
                                />

                                <VisuallyHidden>
                                    {intl.formatMessage(
                                        athenaCommonMessages.close
                                    )}
                                </VisuallyHidden>
                            </SafeguardedLink>
                        </div>
                        {!isResourceSeriesFinished(resourceSeries) && (
                            <NavigationBreadcrumbs
                                isWitchShell={isWitchShell}
                            />
                        )}
                    </div>
                ) : (
                    <>
                        {!isResourceSeriesFinished(resourceSeries) && (
                            <NavigationBreadcrumbs
                                isWitchShell={isWitchShell}
                            />
                        )}
                        <SafeguardedLink
                            to={moduleListUrl}
                            moduleId={moduleId}
                            className="teacher-header__exit"
                            opts={opts}
                            aiType={aiType}
                        >
                            <Icon
                                path="close"
                                size="medium"
                                color={
                                    // TODO #141414 Should be a css variable
                                    isWitchShell ? "#141414" : "white"
                                }
                                className="exit__icon"
                            />
                            <VisuallyHidden>
                                {intl.formatMessage(athenaCommonMessages.close)}
                            </VisuallyHidden>
                        </SafeguardedLink>
                    </>
                )}
            </header>
            {specimen && <PlayerSpecimenBanner />}
        </>
    );
};

const NavigationBreadcrumbs = ({ isWitchShell }: { isWitchShell: boolean }) => {
    const intl = useIntl();
    const data = useData();
    const exercises = useExercises();
    const {
        config: { ai },
    } = useContext(configStore);
    const teacherModuleListShowFullTitle = useFeatureFlag(
        "teacherModuleListShowFullTitle"
    );
    const disableIndexingByCode = useFeatureFlag("disableIndexingByCode");
    const dispatch = useResourceSeriesDispatch();

    const { learningItem } = useResourceSeries();
    const hierarchy = useResourceSeriesMOA();
    const [currentHierarchy, setCurrentHierarchy] = useState({
        ...hierarchy,
        exercise: learningItem,
    });
    useEffect(() => {
        // Store here to avoid glitching when loading resources
        if (
            hierarchy.objective &&
            hierarchy.activity &&
            learningItem &&
            (hierarchy.activity.id !== currentHierarchy.activity?.id ||
                hierarchy.objective.id !== currentHierarchy.objective?.id ||
                hierarchy.module.id !== currentHierarchy.module.id ||
                learningItem.id !== currentHierarchy.exercise?.id)
        )
            setCurrentHierarchy({
                ...hierarchy,
                exercise: learningItem,
            });
    }, [hierarchy, learningItem, currentHierarchy]);

    if (
        !currentHierarchy.objective &&
        !currentHierarchy.activity &&
        !currentHierarchy.exercise
    )
        return null;

    const currentExerciseIndex =
        currentHierarchy.activity?.exerciseIds.findIndex(
            (id) => id === currentHierarchy.exercise?.id
        ) ?? 0;

    return (
        <div
            className="teacher-header__breadcrumbs"
            // Enables horizontal scrolling with mouse wheel
            onWheel={(e: WheelEvent<HTMLDivElement>) => {
                if (e.deltaY === 0) return;
                e.currentTarget.scrollTo({
                    left: e.currentTarget.scrollLeft + e.deltaY,
                });
            }}
        >
            <CustomSelect
                className="teacher-header__breadcrumbs__select"
                label={intl.formatMessage(commonMessages.module)}
                variant="label-on-border"
                color={
                    // TODO #141414 Should be a css variable
                    isWitchShell ? "#141414" : "white"
                }
                colorMode="dark"
                iconProps={dropdownIconProps(isWitchShell)}
                onSelectOption={(moduleId) =>
                    dispatch({
                        type: "goToExercise",
                        hierarchy: firstExerciseInModule(moduleId, data),
                    })
                }
                selectedOptionLabel={generateItemTitle(
                    currentHierarchy.module,
                    "module",
                    data,
                    intl,
                    undefined,
                    ai,
                    teacherModuleListShowFullTitle
                )}
                selectedOptionValue={currentHierarchy.module.id}
            >
                {data.modules
                    .filter(
                        (module) =>
                            module.visibilityStatus === VisibilityStatus.Visible
                    )
                    .map((module) => (
                        <option key={module.id} value={module.id}>
                            {generateItemTitle(
                                module,
                                "module",
                                data,
                                intl,
                                undefined,
                                ai,
                                teacherModuleListShowFullTitle
                            )}
                        </option>
                    ))}
            </CustomSelect>

            {currentHierarchy.objective && (
                <CustomSelect
                    className="teacher-header__breadcrumbs__select"
                    label={intl.formatMessage({
                        id: "player-teacherHeader-objective",
                        defaultMessage: "Objective",
                    })}
                    variant="label-on-border"
                    color={
                        // TODO #141414 Should be a css variable
                        isWitchShell ? "#141414" : "white"
                    }
                    colorMode="dark"
                    iconProps={dropdownIconProps(isWitchShell)}
                    onSelectOption={(objectiveId) => {
                        dispatch({
                            type: "goToExercise",
                            hierarchy: firstExerciseInObjective(
                                currentHierarchy.module.id,
                                objectiveId,
                                data
                            ),
                        });
                    }}
                    selectedOptionLabel={generateItemTitle(
                        currentHierarchy.objective,
                        "objective",
                        data,
                        intl,
                        currentHierarchy.module.objectiveIds,
                        ai,
                        undefined,
                        {
                            disableIndexingByCode: disableIndexingByCode,
                            parentId: currentHierarchy.module.id,
                        }
                    )}
                    selectedOptionValue={currentHierarchy.objective.id}
                >
                    {getSublevelPool<Objective>(
                        currentHierarchy.module.objectiveIds,
                        data.objectives
                    ).map((objective) => (
                        <option key={objective.id} value={objective.id}>
                            {generateItemTitle(
                                objective,
                                "objective",
                                data,
                                intl,
                                currentHierarchy.module.objectiveIds,
                                ai,
                                undefined,
                                {
                                    disableIndexingByCode:
                                        disableIndexingByCode,
                                    parentId: currentHierarchy.module.id,
                                }
                            )}
                        </option>
                    ))}
                </CustomSelect>
            )}

            {currentHierarchy.objective && currentHierarchy.activity && (
                <CustomSelect
                    className="teacher-header__breadcrumbs__select"
                    label={intl.formatMessage({
                        id: "player-teacherHeader-activity",
                        defaultMessage: "Activity",
                    })}
                    variant="label-on-border"
                    color={
                        // TODO #141414 Should be a css variable
                        isWitchShell ? "#141414" : "white"
                    }
                    colorMode="dark"
                    iconProps={dropdownIconProps(isWitchShell)}
                    onSelectOption={(activityId) => {
                        dispatch({
                            type: "goToExercise",
                            hierarchy: firstExerciseInActivity(
                                currentHierarchy.module.id,
                                currentHierarchy.objective!.id,
                                activityId,
                                data
                            ),
                        });
                    }}
                    selectedOptionLabel={generateItemTitle(
                        currentHierarchy.activity,
                        "activity",
                        data,
                        intl,
                        currentHierarchy.objective.activityIds,
                        undefined,
                        undefined,
                        {
                            disableIndexingByCode: true,
                            parentId: currentHierarchy.objective.id,
                        }
                    )}
                    selectedOptionValue={currentHierarchy.activity.id}
                >
                    {getSublevelPool<Activity>(
                        currentHierarchy.objective.activityIds,
                        data.activities
                    ).map((activity) => (
                        <option key={activity.id} value={activity.id}>
                            {generateItemTitle(
                                activity,
                                "activity",
                                data,
                                intl,
                                currentHierarchy.objective!.activityIds,
                                undefined,
                                undefined,
                                {
                                    disableIndexingByCode: true,
                                    parentId: currentHierarchy.objective!.id,
                                }
                            )}
                        </option>
                    ))}
                </CustomSelect>
            )}

            {currentHierarchy.exercise && currentHierarchy.activity && (
                <CustomSelect
                    className="teacher-header__breadcrumbs__select"
                    label={intl.formatMessage({
                        id: "player-teacherHeader-exercise",
                        defaultMessage: "Exercise",
                    })}
                    variant="label-on-border"
                    color={
                        // TODO #141414 Should be a css variable
                        isWitchShell ? "#141414" : "white"
                    }
                    colorMode="dark"
                    iconProps={dropdownIconProps(isWitchShell)}
                    onSelectOption={(exerciseId) => {
                        dispatch({
                            type: "goToExercise",
                            hierarchy: [
                                exerciseId,
                                currentHierarchy.activity!.id,
                                currentHierarchy.objective!.id,
                                currentHierarchy.module.id,
                            ],
                        });
                    }}
                    selectedOptionLabel={`${Case.pascal(
                        intl.formatMessage(commonMessages.exercise)
                    )} ${currentExerciseIndex + 1}`}
                    selectedOptionValue={currentHierarchy.exercise.id}
                >
                    {getSublevelPool<Exercise>(
                        currentHierarchy.activity.exerciseIds,
                        exercises
                    ).map((exercise, index) => (
                        <option key={exercise.id} value={exercise.id}>
                            {`${Case.pascal(
                                intl.formatMessage(commonMessages.exercise)
                            )} ${index + 1}`}
                        </option>
                    ))}
                </CustomSelect>
            )}
        </div>
    );
};

const generateItemTitle = (
    resource: Module | Objective | Activity,
    type: "module" | "objective" | "activity",
    data: Data,
    intl: IntlShape,
    pool?: Module["objectiveIds"] | Objective["activityIds"],
    aiConfig?: Config["ai"],
    showFullTitle?: boolean,
    indexing?: { disableIndexingByCode: boolean; parentId: string }
) => {
    const resourceIndex = getResourceIndex(resource, data, type, indexing);
    const title = resource.title.short;
    if (type === "module") {
        if (showFullTitle) return title;
        return `${resourceIndex}. ${title}`;
    }

    if (type === "objective") {
        let initialTestId = (pool as Module["objectiveIds"]).find(
            initialTestFilter(aiConfig!.initialTest, "isInitialTest")
        );
        let objectiveShortIntl = Case.pascal(
            intl.formatMessage(commonMessages.objectiveShort)
        ).charAt(0);
        if (initialTestId)
            if (initialTestId === resource.id) {
                return `${title}`;
            } else {
                return `${objectiveShortIntl}${resourceIndex}. ${title}`;
            }
        return `${objectiveShortIntl}${resourceIndex}. ${title}`;
    }
    if (type === "activity")
        return `${Case.pascal(
            intl.formatMessage(commonMessages.activityShort).charAt(0)
        )}${resourceIndex}. ${title}`;
    return "";
};

function dropdownIconProps(isWitchShell: boolean) {
    // TODO #141414 Should be a css variable
    return {
        path: "chevron_down",
        size: { width: 24, height: 33.573 },
        color: isWitchShell ? "#141414" : "white",
    };
}

const messages = defineMessages({
    openInfoPanel: {
        id: "player-teacherHeader-a11y-openInfoPanel",
        defaultMessage: "Open the information panel",
    },
    closeInfoPanel: {
        id: "player-teacherHeader-a11y-closeInfoPanel",
        defaultMessage: "Close the information panel",
    },
    progression: {
        id: "player-teacherHeader-a11y-progression",
        defaultMessage: "Progression",
    },
});

export default TeacherHeader;

const firstExerciseInModule = (moduleId: string, data: Data) => {
    const module = getModuleById(moduleId, data);

    const objectiveId = firstVisibleObjectiveInModule(
        module.objectiveIds,
        data
    );
    if (!objectiveId)
        throw new ObjectiveNotVisibleError("No visible objectives were found");

    return firstExerciseInObjective(moduleId, objectiveId, data);
};

const firstVisibleObjectiveInModule = (objectiveIds: string[], data: Data) =>
    objectiveIds.find(
        (objectiveId) =>
            getObjectiveById(objectiveId, data).visibilityStatus !==
            VisibilityStatus.Unavailable
    );

const firstExerciseInObjective = (
    moduleId: string,
    objectiveId: string,
    data: Data
) => {
    const objective = getObjectiveById(objectiveId, data);
    return firstExerciseInActivity(
        moduleId,
        objectiveId,
        objective.activityIds[0],
        data
    );
};

const firstExerciseInActivity = (
    moduleId: string,
    objectiveId: string,
    activityId: string,
    data: Data
) => {
    const activity = getActivityById(activityId, data);
    return moaeToHierarchy({
        exerciseId: activity.exerciseIds[0],
        activityId: activity.id,
        objectiveId,
        moduleId,
    });
};
